import React from 'react';

// ==================== HELPERS ====================

// Compatibilidad con datos guardados antes de soportar múltiples partidas
// (partidaPrograma era un objeto singular {nombre, fechaInicio}, no un array).
export function normalizarPartidas(valor) {
  if (!valor) return [];
  if (Array.isArray(valor)) return valor;
  if (typeof valor === 'object' && valor.nombre) return [valor];
  return [];
}

export function fechaMasTemprana(partidas) {
  const fechas = (partidas || [])
    .map(p => p.fechaInicio)
    .filter(Boolean)
    .sort();
  return fechas[0] || '';
}

export function nombreParaEspecialidad(partidas) {
  return (partidas && partidas[0]?.nombre) || '';
}

// Construye { [wbsCode]: actividad } a partir de la lista COMPLETA de actividades del
// Programa (incluye nodos padre/ancestros), tal como la devuelve obtenerActividadesKpi().
export function construirActMapPrograma(acts) {
  const map = {};
  for (const a of (acts || [])) {
    if (a.wbsCode) map[a.wbsCode] = a;
  }
  return map;
}

// Dado el wbsCode de una partida hoja, devuelve el grupo "nivel 2" del WBS
// (ej. "1.4.1.1.1.4" -> "1.4 Edificación") buscando el nodo ancestro en actMap.
// Si no se encuentra ese nivel, cae al propio wbsCode.
export function grupoNivel2FromWbs(wbsCode, actMap) {
  if (!wbsCode) return '';
  const partes = wbsCode.split('.');
  const code = partes.slice(0, 2).join('.');
  const act = (actMap && (actMap[code] || actMap[wbsCode])) || null;
  if (act) return `${act.wbsCode === code ? code : wbsCode} ${act.nombre}`.trim();
  return wbsCode;
}

// Especialidad/sección a usar para un item creado "Desde Programa": deriva el grupo WBS
// nivel 2 de la partida elegida; si no hay wbsCode (datos históricos) cae al nombre de la
// partida hoja, igual que antes.
export function especialidadDesdePartida(partidas, actMap) {
  const primera = partidas && partidas[0];
  if (primera?.wbsCode) {
    const grupo = grupoNivel2FromWbs(primera.wbsCode, actMap);
    if (grupo) return grupo;
  }
  return nombreParaEspecialidad(partidas);
}

// Filtra hojas del WBS (nodos sin hijos) a partir de la lista completa de actividades del
// Programa, deduplica por nombre (se queda con la de fecha de inicio más temprana) y
// ordena por `orden`. Conserva wbsCode en cada hoja resultante.
export function obtenerHojasProgramaDedup(acts) {
  const lista = acts || [];
  const wbsSet = new Set(lista.map(a => a.wbsCode).filter(Boolean));
  const hojas = lista.filter(a => {
    if (!a.wbsCode) return true;
    const prefix = a.wbsCode + '.';
    for (const c of wbsSet) if (c.startsWith(prefix)) return false;
    return true;
  });
  const dedup = {};
  for (const h of hojas) {
    if (!h.nombre) continue;
    const prev = dedup[h.nombre];
    if (!prev || new Date(h.fechaInicioBase) < new Date(prev.fechaInicioBase)) dedup[h.nombre] = h;
  }
  return Object.values(dedup).sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
}

// ==================== COMPONENTE ====================
// Selector de una única partida del Programa: chip de la partida elegida (si hay) +
// buscador/dropdown para elegirla o cambiarla.
export default function SelectorPartidasPrograma({ actividadesDisponibles, seleccionadas, onCambiar, onAbrir }) {
  const [search, setSearch] = React.useState('');
  const [abierto, setAbierto] = React.useState(false);

  const nombresSeleccionados = new Set(seleccionadas.map(p => p.nombre));

  const agregar = (actividad) => {
    const fechaStr = actividad.fechaInicioBase ? actividad.fechaInicioBase.split('T')[0] : '';
    onCambiar([{ nombre: actividad.nombre, fechaInicio: fechaStr, wbsCode: actividad.wbsCode || '' }]);
    setSearch('');
    setAbierto(false);
  };

  const quitar = (nombre) => {
    onCambiar(seleccionadas.filter(p => p.nombre !== nombre));
  };

  const disponibles = (actividadesDisponibles || [])
    .filter(a => !nombresSeleccionados.has(a.nombre))
    .filter(a => a.nombre.toLowerCase().includes(search.toLowerCase()));

  const yaHaySeleccion = seleccionadas.length > 0;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {seleccionadas.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
          {seleccionadas.map(p => (
            <span
              key={p.nombre}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '999px', fontSize: '0.78rem', color: '#111827' }}
            >
              {p.nombre}
              {p.fechaInicio && <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>({p.fechaInicio})</span>}
              <button
                type="button"
                onClick={() => quitar(p.nombre)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9ca3af', fontSize: '0.85rem', lineHeight: 1, padding: 0 }}
                title="Quitar partida"
              >×</button>
            </span>
          ))}
        </div>
      )}

      {!yaHaySeleccion && (
        <div
          onClick={() => { setAbierto(v => !v); if (!abierto && onAbrir) onAbrir(); }}
          style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: 'white', fontSize: '0.85rem', color: '#9ca3af', userSelect: 'none' }}
        >
          + Elegir partida del programa
        </div>
      )}

      {abierto && !yaHaySeleccion && (
        <div
          onClick={() => setAbierto(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: 'min(700px, 92vw)', maxHeight: '75vh', background: 'white', borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Seleccionar partida del Programa</h3>
              <button
                type="button"
                onClick={() => setAbierto(false)}
                style={{ border: 'none', background: '#f3f4f6', color: '#374151', width: 28, height: 28, borderRadius: '6px', cursor: 'pointer', fontSize: '0.95rem', lineHeight: 1 }}
              >✕</button>
            </div>

            <input
              autoFocus
              placeholder="Buscar partida..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '14px 20px', border: 'none', borderBottom: '1px solid #e2e8f0', outline: 'none', fontSize: '0.95rem', boxSizing: 'border-box', flexShrink: 0 }}
            />

            <div style={{ overflowY: 'auto' }}>
              {disponibles.map(a => {
                const fechaStr = a.fechaInicioBase ? a.fechaInicioBase.split('T')[0] : '';
                return (
                  <div
                    key={a.wbsCode || a.nombre}
                    onClick={() => agregar(a)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.95rem' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fff7ed'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <span style={{ color: '#1e293b' }}>{a.nombre}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.82rem', marginLeft: 12, whiteSpace: 'nowrap' }}>{fechaStr}</span>
                  </div>
                );
              })}
              {disponibles.length === 0 && (
                <div style={{ padding: '32px 20px', color: '#9ca3af', fontSize: '0.9rem', textAlign: 'center' }}>Sin resultados</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
